import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "../../lib/api";
import { BarChart3 } from "lucide-react";

export const PERIODS = [
  ["yesterday", "Dün"],
  ["last_week", "Geçen hafta"],
  ["last_15_days", "Son 15 gün"],
  ["this_month", "Bu ay"],
  ["last_3_months", "Son 3 ay"],
  ["all_time", "Tüm zamanlar"],
];
const NO_DATA = "Henüz yeterli veri yok";

const pct = (v) => (v == null ? null : `%${Math.round(v * 100)}`);
const fmtDate = (s) => (s ? new Date(s).toLocaleDateString("tr-TR", { timeZone: "Europe/Istanbul" }) : "");

function Delta({ value, suffix = "", tid }) {
  if (value == null) return null;
  const cls = value > 0 ? "text-emerald-600" : value < 0 ? "text-rose-600" : "text-slate-400";
  return (
    <span className={`text-xs font-medium ${cls}`} data-testid={tid}>
      {value > 0 ? "+" : ""}{value}{suffix}
    </span>
  );
}

function Card({ label, value, sub, tid, children }) {
  return (
    <div className="rounded-lg border border-[#E2E8F0] bg-slate-50 p-3 min-w-0">
      <div className="text-[11px] text-slate-500">{label}</div>
      <div className="font-display text-xl text-[#0B1A35] mt-0.5 break-words" data-testid={tid}>{value}</div>
      {sub && <div className="text-[11px] text-slate-500 mt-0.5">{sub}</div>}
      {children}
    </div>
  );
}

export function ClassAnalytics({ classId }) {
  const [period, setPeriod] = useState("last_15_days");
  const [summary, setSummary] = useState(null);
  const [students, setStudents] = useState([]);
  const [error, setError] = useState(null);

  useEffect(() => {
    (async () => {
      setError(null);
      try {
        const [s, st] = await Promise.all([
          api.get(`/teacher/classes/${classId}/analytics/summary`, { params: { period } }),
          api.get(`/teacher/classes/${classId}/analytics/students`, { params: { period } }),
        ]);
        setSummary(s.data);
        setStudents(st.data.students);
      } catch (e) {
        setError(e?.response?.data?.detail || "Analiz yüklenemedi");
      }
    })();
  }, [classId, period]);

  const m = summary?.metrics;
  const cmp = summary?.comparison;
  return (
    <div className="space-y-3 pt-2 border-t border-slate-100" data-testid={`class-analytics-${classId}`}>
      <div className="flex items-center gap-2 text-sm font-medium text-[#0B1A35]">
        <BarChart3 size={15} className="text-[#007BFF]" /> Sınıf Performansı
      </div>
      <div className="flex flex-wrap gap-1.5" data-testid="class-analytics-periods">
        {PERIODS.map(([k, l]) => (
          <button key={k} onClick={() => setPeriod(k)} data-testid={`class-analytics-period-${k}`}
            className={`px-2.5 py-1 rounded-full text-xs border transition-colors ${
              period === k ? "bg-[#0B1A35] text-white border-[#0B1A35]" : "border-[#E2E8F0] text-slate-600 hover:border-[#007BFF]"}`}>
            {l}
          </button>
        ))}
      </div>
      {error && <div className="text-xs text-rose-600" data-testid="class-analytics-error">{error}</div>}
      {summary && (
        <>
          <div className="text-[11px] text-slate-500" data-testid="class-analytics-range">
            {summary.period.start ? `${fmtDate(summary.period.start)} – ${fmtDate(summary.period.end)}` : "Sınıfa katılımdan bugüne"}
            {cmp ? ` · önceki dönemle karşılaştırılıyor` : " · önceki dönem karşılaştırması yok"}
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
            <Card label="Çözülen soru" value={m.total_questions} tid="class-analytics-total">
              {cmp && <Delta value={cmp.questions_delta} tid="class-analytics-total-delta" />}
            </Card>
            <Card label="Doğru / Yanlış / Boş" value={`${m.correct} / ${m.wrong} / ${m.blank}`} tid="class-analytics-dyb" />
            <Card label="Başarı oranı" value={pct(m.success_rate) ?? <span className="text-sm text-slate-400">{NO_DATA}</span>}
              sub="doğru ÷ (doğru + yanlış)" tid="class-analytics-rate">
              {cmp && <Delta value={cmp.success_rate_delta_pp} suffix=" puan" tid="class-analytics-rate-delta" />}
            </Card>
            <Card label="Ağırlıklı başarı" value={pct(m.weighted_success_rate) ?? <span className="text-sm text-slate-400">{NO_DATA}</span>}
              sub="konu önemine göre ağırlıklı" tid="class-analytics-weighted" />
            <Card label="Soru çözen öğrenci" value={`${summary.active_students} / ${summary.member_count}`} tid="class-analytics-active" />
          </div>
          <StudentTable students={students} hasComparison={!!cmp} classId={classId} />
        </>
      )}
    </div>
  );
}

function StudentTable({ students, hasComparison, classId }) {
  if (students.length === 0) {
    return <div className="text-xs text-slate-400 italic" data-testid="class-analytics-no-students">Sınıfta henüz öğrenci yok.</div>;
  }
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm" data-testid="class-analytics-students">
        <thead className="text-[11px] text-slate-500 text-left">
          <tr>
            <th className="py-1.5 pr-2 font-normal">Öğrenci</th>
            <th className="py-1.5 pr-2 font-normal">Soru</th>
            <th className="py-1.5 pr-2 font-normal">Başarı</th>
            <th className="py-1.5 font-normal">Ağırlıklı</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100">
          {students.map((s) => (
            <tr key={s.student_user_id} data-testid={`class-analytics-student-${s.student_user_id}`}>
              <td className="py-2 pr-2 min-w-[120px]">
                <Link to={`/ogretmen/sinif/${classId}/ogrenci/${s.student_user_id}`} className="block truncate text-[#007BFF] hover:underline"
                  data-testid={`class-analytics-student-link-${s.student_user_id}`}>{s.name}</Link>
                <div className="text-[11px] text-slate-400">D {s.metrics.correct} · Y {s.metrics.wrong} · B {s.metrics.blank}</div>
              </td>
              <td className="py-2 pr-2 whitespace-nowrap">
                {s.metrics.total_questions}{" "}
                {hasComparison && <Delta value={s.comparison?.questions_delta} />}
              </td>
              <td className="py-2 pr-2 whitespace-nowrap" data-testid={`class-analytics-student-rate-${s.student_user_id}`}>
                {s.metrics.success_rate == null
                  ? <span className="text-xs text-slate-400">{NO_DATA}</span>
                  : <>{pct(s.metrics.success_rate)} {hasComparison && <Delta value={s.comparison?.success_rate_delta_pp} suffix=" p" />}</>}
              </td>
              <td className="py-2 whitespace-nowrap text-slate-600">
                {s.metrics.weighted_success_rate == null ? <span className="text-xs text-slate-400">—</span> : pct(s.metrics.weighted_success_rate)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
